<template>
  <div>
    <div>
      <div v-show="isLoading" class="u-align-center u-pad-h--5 u-pad-top--5" style="min-height: 300px">
        <center><LoadingSpinner :style="{ 'margin-top': loaderMarginTop }" /></center>
      </div>
      <div v-show="!isLoading">
        <div :class="{ 'u-mrgn-top--4 u-mrgn-bottom--4 u-pad-top--4 u-border--top': isProductContent }">
          <div v-if="title">
            <a :href="productUrl" data-no-turbolink="true" class="u-fg--black u-txt--bold u-txt--fair qa-chart__title">
              {{ title }}
            </a>
          </div>
          <div v-if="showTopRangeSelector" class=" u-mrgn-bottom--2 o-layout">
            <div
              v-for="(range, index) in ranges"
              :key="range"
              class="o-layout__item u-1of4 qa-chart__range-option"
              :disabled="isChartLoading"
              :class="{
                'u-fg--red-brand': index == selectedRangeIdx,
                [`qa-chart__range-option-${index}`]: true,
              }"
              @click="setRange(index)"
            >
              {{ rangesText[index] }}
            </div>
          </div>
          <div class=" u-clearfix">
            <div class="u-float-left u-pad-top--1 qa-chart__date-range">
              <span class="qa-chart__start-date"> {{ firstDate }} </span> -
              <span class="qa-chart__end-date"> {{ lastDate }} </span>
            </div>
            <div v-if="showUpperRightRangeSelector" class="u-float-right">
              <div class="c-btn-group">
                <button
                  v-for="(range, index) in ranges"
                  :key="range"
                  :disabled="isChartLoading"
                  :class="{
                    'u-bg--sand is-active': index == selectedRangeIdx,
                    [`qa-chart__range-option-${index}`]: true,
                  }"
                  class="c-btn qa-chart__range-option"
                  type="button"
                  @click="setRange(index)"
                >
                  {{ rangesText[index] }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div :class="{ 'u-pad-h--5': isProductContent }" class="u-pad-top--2">
          <div :id="chartId" :style="{ height: chartHeight }" class="u-width-1" />
        </div>
        <div v-if="showBottomRangeSelector" class="o-layout u-mrgn-top--4 u-mrgn-bottom--4">
          <div class="o-layout__item u-10of12 u-push-1of12 c-btn-group u-align-center">
            <button
              v-for="(range, index) in ranges"
              :key="range"
              :disabled="isChartLoading"
              :class="{
                'c-btn--red is-active': index == selectedRangeIdx && showRedButton,
                'u-bg--sand is-active': index == selectedRangeIdx && !showRedButton,
              }"
              class="c-btn u-txt--normal u-width-1of4"
              type="button"
              @click="setRange(index)"
            >
              {{ rangesText[index] }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Highcharts from 'highcharts';
import dayjs from 'dayjs';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { trackEvent } from 'shared/helpers/trackerHelper';
import { generateSeoProductUrl } from 'shared/helpers/seoMap';

export default {
  name: 'ProductChart',
  components: {
    LoadingSpinner,
  },
  props: {
    isMweb: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      required: true,
    },
    ranges: {
      type: Array,
      default() {
        return [7, 30, 90, 365];
      },
    },
    rangesText: {
      type: Array,
      default() {
        return ['7 Hari', '1 Bulan', '3 Bulan', '1 Tahun'];
      },
    },
    showTopRangeSelector: {
      type: Boolean,
      default: false,
    },
    showBottomRangeSelector: {
      type: Boolean,
      default: false,
    },
    showUpperRightRangeSelector: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    showDate: {
      type: Boolean,
      default: true,
    },
    chartHeight: {
      type: String,
      default: '400px',
    },
    loaderMarginTop: {
      type: String,
      default: '125px',
    },
    isProductContent: {
      type: Boolean,
      default: false,
    },
    showRedButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chart: {},
      isLoading: true,
      isChartLoading: true,
      selectedRangeIdx: 0,
      navs: [],
      xAxis: [],
      selectedNavs: [],
      navChartConfig: {
        chart: {
          type: 'area',
        },
        title: { text: null },
        legend: { enabled: false },
        xAxis: { categories: [] },
        yAxis: { title: { enabled: false } },
        series: [
          {
            name: 'Harga NAB/Unit',
            data: [],
            dataGrouping: { enabled: false },
            states: { hover: { lineWidth: 1.5 } },
          },
        ],
        rangeSelector: { enabled: false },
        navigator: {
          maskFill: 'rgba(226,107,141,.1)',
          series: {
            color: '#e26b8d',
            fillColor: '#fff',
          },
        },
        plotOptions: {
          series: {
            color: '#e26b8d',
            lineColor: '#e26b8d',
          },
          area: {
            fillOpacity: 0.1,
          },
        },
        tooltip: {
          backgroundColor: '#000',
          borderColor: '#000',
          style: {
            color: '#fff',
            whiteSpace: 'normal',
            width: '275px',
          },
        },
        credits: { enabled: false },
      },
    };
  },
  computed: {
    minNav() {
      let min = 0;
      if (this.selectedNavs.length) {
        min = this.selectedNavs.reduce((a, b) => Math.min(a, b));
      }
      return min;
    },
    maxNav() {
      let max = 0;
      if (this.selectedNavs.length) {
        max = this.selectedNavs.reduce((a, b) => Math.max(a, b));
      }
      return max;
    },
    softMinNav() {
      return this.minNav - 0.000001 * this.minNav;
    },
    softMaxNav() {
      return this.maxNav + 0.000001 * this.maxNav;
    },
    lastDate() {
      if (this.navs.length) {
        return dayjs(this.navs[this.navs.length - 1].date).format('DD MMMM YYYY');
      }
      return '';
    },
    firstDate() {
      if (this.navs.length) {
        return dayjs(this.navs[0].date).format('DD MMMM YYYY');
      }
      return '';
    },
    chartId() {
      if (this.product) {
        return `js-bukareksa-price-chart-${this.product.id}`;
      }
      return '';
    },
    productUrl() {
      return generateSeoProductUrl(this.product.isin_code);
    },
  },
  watch: {
    selectedRangeIdx() {
      this.getNav();
    },
    product() {
      this.isLoading = true;
      this.getNav();
    },
  },
  mounted() {
    if (this.product) {
      this.chart = Highcharts.chart(document.getElementById(this.chartId), this.navChartConfig);
      this.getNav();
    }
  },
  methods: {
    getNav() {
      this.isChartLoading = true;
      const navParams = {
        id: this.product.id,
        date: dayjs().format('YYYY-MM-DD'),
        limit: this.ranges[this.selectedRangeIdx],
      };
      this.$api
        .getProductNav(navParams)
        .then(response => {
          this.isLoading = false;
          this.isChartLoading = false;
          this.navs = response.data.reverse();
          this.setupData();
          this.setupChart();
          this.trackNavs(navParams, 'success');
        })
        .catch(() => {
          this.trackNavs(navParams, 'failed');
        });
    },

    setupData() {
      const component = this;
      this.xAxis = [];
      this.selectedNavs = [];
      this.navs.forEach(nav => {
        component.xAxis.push(dayjs(nav.date).format('DD/MM'));
        component.selectedNavs.push(nav.value);
      });
      this.navChartConfig.xAxis.categories = this.xAxis;
      this.navChartConfig.series[0].data = this.selectedNavs;
      this.navChartConfig.series[0].name = this.product.name;
      this.navChartConfig.yAxis.min = this.softMinNav;
      this.navChartConfig.yAxis.max = this.softMaxNav;
    },

    setupChart() {
      const component = this;
      this.$nextTick(() => component.chart.update(component.navChartConfig, true));
    },

    setRange(idx) {
      this.selectedRangeIdx = idx;
    },

    trackNavs(param, state) {
      const trackerPayload = {
        evn: 'bukareksa_navs_api',
        product_name: this.product.name,
        range: param.limit,
        state,
      };

      trackEvent(trackerPayload, { caller: this.isMweb ? 'mweb' : 'dweb', product: 'bukareksa', withReferrer: false });
    },
  },
};
</script>
